import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
    HomeIcon,
    CogIcon,
    ChartBarIcon,
    UserIcon,
    UserGroupIcon,
    ArrowRightOnRectangleIcon,
    ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import BotControls from '../components/bot/BotControls';
import TradingSettings from '../components/trading/TradingSettings';
import TradingData from '../components/trading/TradingData';
import UserSettings from '../components/user/UserSettings';
import UserManagement from '../components/admin/UserManagement';
import { useAuth } from '../contexts/AuthContext';
import * as botService from '../services/botService';
import FloatingZAIIcon from "../AI-COMPONENT/FloatingZAIIcon";

const DashboardPage = () => {
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('bot');
    const [botStatus, setBotStatus] = useState({ isCapturing: false, message: '' });
    const [tradingAmount, setTradingAmount] = useState(0);
    const [trendTracker, setTrendTracker] = useState(5);
    const [directions, setDirections] = useState([]);
    const [ongoingTrade, setOngoingTrade] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

    useEffect(() => {
        if (!currentUser) {
            navigate('/login');
            return;
        }

        const fetchInitialData = async () => {
            try {
                setIsLoading(true);
                setError('');

                // Fetch data in parallel
                const [statusData, directionsData, amountData, trendResponse] = await Promise.all([
                    botService.getBotStatus().catch(e => {
                        // console.error('Error fetching bot status:', e);
                        return { isCapturing: false };
                    }),
                    botService.getDirections().catch(e => {
                        // console.error('Error fetching directions:', e);
                        return [];
                    }),
                    botService.getAmount().catch(e => {
                        // console.error('Error fetching amount:', e);
                        return 0;
                    }),
                    botService.getTrendTracker().catch(e => {
                        // console.error('Error fetching trend tracker:', e);
                        return { number_of_directions: 5 };
                    })
                ]);

                // Handle response formats
                const directionsArray = Array.isArray(directionsData)
                    ? directionsData
                    : directionsData?.directions || [];

                setBotStatus({
                    isCapturing: statusData.isCapturing || false,
                    message: statusData.message || ''
                });
                setDirections(directionsArray);
                setTradingAmount(amountData);
                // Extract trend tracker value from the response
                const trendValue = trendResponse?.number_of_directions ?? 
                                 (typeof trendResponse === 'number' ? trendResponse : 5);
                setTrendTracker(trendValue);

                // Get ongoing trade if available
                try {
                    const tradeData = await botService.getOngoingTrade();
                    setOngoingTrade(tradeData?.trade || tradeData || null);
                } catch (tradeErr) {
                    // console.log('No ongoing trade or error fetching:', tradeErr);
                    setOngoingTrade(null);
                }

            } catch (err) {
                const errorMessage = err.response?.data?.message || 'Failed to load dashboard data. Please try refreshing the page.';
                setError(errorMessage);
                toast.error(errorMessage);
                // console.error('Dashboard error:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialData();

        // Set up polling for real-time updates (every 10 seconds)
        // const intervalId = setInterval(fetchInitialData, 10000);
        //
        // // Clean up interval on component unmount
        // return () => clearInterval(intervalId);
    }, [currentUser, navigate]);

    const handleStartBot = async () => {
        try {
            setIsLoading(true);
            const result = await botService.startBot();
            if (result.success) {
                setBotStatus({
                    isCapturing: true,
                    message: 'Bot started successfully'
                });
                toast.success('Bot started successfully');
            } else {
                const errorMsg = result.message || 'Failed to start bot';
                setError(errorMsg);
                toast.error(errorMsg);
            }
        } catch (err) {
            const errorMsg = 'Failed to start bot. Please try again.';
            setError(errorMsg);
            toast.error(errorMsg);
            // console.error('Start bot error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStopBot = async () => {
        try {
            setIsLoading(true);
            const result = await botService.stopBot();
            if (result.success) {
                setBotStatus({
                    isCapturing: false,
                    message: 'Bot stopped successfully'
                });
                toast.success('Bot stopped successfully');
            } else {
                const errorMsg = result.message || 'Failed to stop bot';
                setError(errorMsg);
                toast.error(errorMsg);
            }
        } catch (err) {
            const errorMsg = 'Failed to stop bot. Please try again.';
            setError(errorMsg);
            toast.error(errorMsg);
            // console.error('Stop bot error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRestartBot = async () => {
        try {
            setIsLoading(true);
            const result = await botService.restartBot();
            if (result.success) {
                setBotStatus({
                    isCapturing: true,
                    message: 'Bot restarted successfully'
                });
                toast.success('Bot restarted successfully');
            } else {
                const errorMsg = result.message || 'Failed to restart bot';
                setError(errorMsg);
                toast.error(errorMsg);
            }
        } catch (err) {
            const errorMsg = 'Failed to restart bot. Please try again.';
            setError(errorMsg);
            toast.error(errorMsg);
            // console.error('Restart bot error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateSettings = async (newSettings) => {
        console.log('handleUpdateSettings called with:', newSettings);
        try {
            setIsLoading(true);
            setError('');
            let updated = false;

            // Update amount if changed
            if (newSettings.amount !== undefined && newSettings.amount !== tradingAmount) {
                // console.log('Updating amount to:', newSettings.amount);
                const response = await botService.setAmount(newSettings.amount);
                if (response && response.success) {
                    setTradingAmount(newSettings.amount);
                    updated = true;
                } else {
                    throw new Error(response?.message || 'Failed to update trading amount');
                }
            }

            // Update trend tracker if changed
            if (newSettings.trendTracker !== undefined && newSettings.trendTracker !== trendTracker) {
                // console.log('Updating trend tracker to:', newSettings.trendTracker);
                const response = await botService.setTrendTracker(Number(newSettings.trendTracker));
                // console.log('Trend tracker response:', response);
                if (response) {
                    // Handle both direct response and response.data formats
                    const responseData = response.data || response;
                    // Use number_of_directions from response if available, otherwise fall back to the value we sent
                    const newValue = responseData.number_of_directions ?? responseData.numberOfDirections ?? newSettings.trendTracker;
                    // console.log('Setting trend tracker to:', newValue);
                    setTrendTracker(Number(newValue));
                    updated = true;
                } else {
                    throw new Error(response?.message || 'Failed to update trend tracker');
                }
            }

            if (updated) {
                toast.success('Settings updated successfully');
            }
            return { success: true };

        } catch (err) {
            const errorMessage = err.message || 'Failed to update settings';
            // console.error('Update settings error:', err);
            setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            toast.success('Logged out successfully');
            navigate('/login');
        } catch (err) {
            const errorMsg = 'Failed to log out. Please try again.';
            setError(errorMsg);
            toast.error(errorMsg);
            // console.error('Logout error:', err);
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'bot':
                return (
                    <div className="space-y-6">
                        <BotControls
                            isRunning={botStatus.isCapturing}
                            onStart={handleStartBot}
                            onStop={handleStopBot}
                            onRestart={handleRestartBot}
                            showRestart={true}
                        />
                        <TradingData
                            directions={directions}
                            ongoingTrade={ongoingTrade}
                        />
                    </div>
                );
            case 'trading':
                return (
                    <div className="space-y-6">
                        <TradingSettings
                            amount={tradingAmount}
                            trendTracker={trendTracker}
                            onUpdate={handleUpdateSettings}
                            isLoading={isLoading}
                        />
                    </div>
                );
            case 'user':
                return (
                    <UserSettings
                        user={currentUser}
                        onLogout={handleLogout}
                    />
                );
            case 'admin':
                if (!isAdmin) {
                    navigate('/dashboard');
                    return null;
                }
                return <UserManagement />;
            default:
                return null;
        }
    };

    if (isLoading && !botStatus) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
            <FloatingZAIIcon />
            {/* Sidebar */}
            <div className="hidden md:flex md:flex-shrink-0">
                <div className="flex flex-col w-64 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <div className="flex items-center justify-center h-16 px-4 bg-gray-100 dark:bg-gray-900">
                        <h1 className="text-xl font-bold text-blue-600 dark:text-blue-400">ZIZ Bot</h1>
                    </div>
                    <nav className="flex-1 px-2 py-4 space-y-1">
                        <button
                            onClick={() => setActiveTab('bot')}
                            className={`${
                                activeTab === 'bot'
                                    ? 'bg-gray-100 text-blue-600 dark:bg-gray-700 dark:text-blue-400'
                                    : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                            } group flex items-center px-4 py-2 text-sm font-medium rounded-md w-full`}
                        >
                            <ArrowsRightLeftIcon className="mr-3 h-6 w-6" />
                            Bot Controls
                        </button>
                        <button
                            onClick={() => setActiveTab('trading')}
                            className={`${
                                activeTab === 'trading'
                                    ? 'bg-gray-100 text-blue-600 dark:bg-gray-700 dark:text-blue-400'
                                    : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                            } group flex items-center px-4 py-2 text-sm font-medium rounded-md w-full`}
                        >
                            <CogIcon className="mr-3 h-6 w-6" />
                            Trading Settings
                        </button>
                        {isAdmin && (
                            <button
                                onClick={() => setActiveTab('admin')}
                                className={`${
                                    activeTab === 'admin'
                                        ? 'bg-gray-100 text-blue-600 dark:bg-gray-700 dark:text-blue-400'
                                        : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                                } group flex items-center px-4 py-2 text-sm font-medium rounded-md w-full`}
                            >
                                <UserGroupIcon className="mr-3 h-6 w-6" />
                                User Management
                            </button>
                        )}
                        <button
                            onClick={() => setActiveTab('user')}
                            className={`${
                                activeTab === 'user'
                                    ? 'bg-gray-100 text-blue-600 dark:bg-gray-700 dark:text-blue-400'
                                    : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                            } group flex items-center px-4 py-2 text-sm font-medium rounded-md w-full`}
                        >
                            <UserIcon className="mr-3 h-6 w-6" />
                            User Settings
                        </button>
                    </nav>
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            onClick={handleLogout}
                            className="group flex items-center w-full px-4 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                            <ArrowsRightLeftIcon className="mr-3 h-5 w-5" />
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile bottom navigation */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-10">
                <div className="flex justify-around">
                    <button
                        onClick={() => setActiveTab('bot')}
                        className={`${
                            activeTab === 'bot'
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-gray-500 dark:text-gray-400'
                        } flex flex-col items-center justify-center py-2 px-4`}
                    >
                        <HomeIcon className="h-6 w-6" />
                        <span className="text-xs mt-1">Bot</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('trading')}
                        className={`${
                            activeTab === 'trading'
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-gray-500 dark:text-gray-400'
                        } flex flex-col items-center justify-center py-2 px-4`}
                    >
                        <ChartBarIcon className="h-6 w-6" />
                        <span className="text-xs mt-1">Trading</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('user')}
                        className={`${
                            activeTab === 'user'
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-gray-500 dark:text-gray-400'
                        } flex flex-col items-center justify-center py-2 px-4`}
                    >
                        <UserIcon className="h-6 w-6" />
                        <span className="text-xs mt-1">User</span>
                    </button>
                </div>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col overflow-hidden pb-16 md:pb-0">
                {/* Top navigation */}
                <header className="bg-white dark:bg-gray-800 shadow-sm z-10">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex justify-between items-center">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                                {activeTab === 'bot' && 'Bot Controls'}
                                {activeTab === 'trading' && 'Trading Settings'}
                                {activeTab === 'user' && 'User Settings'}
                                {activeTab === 'admin' && 'User Management'}
                            </h1>
                            <div className="flex items-center">
                <span className="text-sm text-gray-600 dark:text-gray-300 mr-4">
                  {currentUser?.email}
                </span>
                                <button
                                    onClick={handleLogout}
                                    className="hidden md:flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                                >
                                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-1" />
                                    Logout
                                </button>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-y-auto focus:outline-none">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        {error && (
                            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md">
                                {error}
                            </div>
                        )}
                        {renderContent()}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default DashboardPage;
